import { useCallback, useEffect, useState } from "react";

import {
  ApiError,
  approveWorkspaceRequest,
  declineWorkspaceRequest,
  fetchPendingWorkspaceRequests,
  fetchTenantCatalog,
  type PendingWorkspaceRequest,
  type TenantCatalogEntry,
} from "./api.js";

/** The replaceable human approval surface from `ACC-005`. */
export const AdminRequests = (): React.JSX.Element => {
  const [requests, setRequests] = useState<readonly PendingWorkspaceRequest[]>();
  const [error, setError] = useState<string>();
  const [busyUser, setBusyUser] = useState<string>();
  const [tenants, setTenants] = useState<readonly TenantCatalogEntry[]>();
  const [nextCursor, setNextCursor] = useState<string>();
  const [loadingTenants, setLoadingTenants] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    setError(undefined);
    try {
      setRequests(await fetchPendingWorkspaceRequests());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not load requests.");
    }
  }, []);

  const loadTenants = useCallback(async (cursor?: string): Promise<void> => {
    setError(undefined);
    setLoadingTenants(true);
    try {
      const page = await fetchTenantCatalog(cursor);
      setTenants((current) => {
        const combined =
          cursor === undefined
            ? [...page.tenants, ...(current ?? [])]
            : [...(current ?? []), ...page.tenants];
        return combined.filter(
          (tenant, index) =>
            combined.findIndex((item) => item.tenantId === tenant.tenantId) ===
            index,
        );
      });
      setNextCursor(page.nextCursor);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not load tenants.");
    } finally {
      setLoadingTenants(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadTenants();
  }, [load, loadTenants]);

  const decide = async (
    request: PendingWorkspaceRequest,
    decision: "approve" | "decline",
    form: HTMLFormElement,
  ): Promise<void> => {
    setError(undefined);
    setBusyUser(request.userId);
    try {
      if (decision === "approve") {
        const approved = await approveWorkspaceRequest(request.userId);
        setTenants((current) => [
          {
            tenantId: approved.tenantId,
            primaryAccountUserId: approved.userId,
            primaryAccountEmail: approved.email,
            createdAt: approved.decidedAt,
          },
          ...(current ?? []).filter(
            (tenant) => tenant.tenantId !== approved.tenantId,
          ),
        ]);
      } else {
        const values = new FormData(form);
        await declineWorkspaceRequest(
          request.userId,
          String(values.get("reason") ?? "").trim(),
        );
      }
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "The decision was not saved.");
    } finally {
      setBusyUser(undefined);
    }
  };

  return (
    <>
      <section className="panel admin-requests" aria-labelledby="requests-heading">
        <h2 id="requests-heading">Workspace requests</h2>
        <p className="notice">
          Approval creates the workspace and permanently assigns a generated
          tenant id.
        </p>

        {error !== undefined && (
          <p className="blocked" role="alert">
            {error}
          </p>
        )}

        {requests === undefined ? (
          <p aria-busy="true">Loading requests…</p>
        ) : requests.length === 0 ? (
          <p>No pending requests.</p>
        ) : (
          <ul className="request-list">
            {requests.map((request) => (
              <li key={request.userId}>
                <h3>{request.email}</h3>
                <p className="notice">
                  Requested {new Date(request.createdAt).toLocaleString()}
                </p>

                <form
                  className="decision approve-decision"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void decide(request, "approve", event.currentTarget);
                  }}
                >
                  <button type="submit" disabled={busyUser === request.userId}>
                    Approve
                  </button>
                </form>

                <form
                  className="decision"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void decide(request, "decline", event.currentTarget);
                  }}
                >
                  <label htmlFor={`reason-${request.userId}`}>Decline reason</label>
                  <input
                    id={`reason-${request.userId}`}
                    name="reason"
                    required
                  />
                  <button type="submit" disabled={busyUser === request.userId}>
                    Decline
                  </button>
                </form>
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="panel tenant-catalog" aria-labelledby="tenants-heading">
        <h2 id="tenants-heading">All tenants</h2>
        <p className="notice">
          Every created workspace remains here after its request leaves the queue.
        </p>

        {tenants === undefined ? (
          <p aria-busy="true">Loading tenants…</p>
        ) : tenants.length === 0 ? (
          <p>No tenants have been created.</p>
        ) : (
          <ul className="tenant-list">
            {tenants.map((tenant) => (
              <li key={tenant.tenantId}>
                <h3>{tenant.primaryAccountEmail}</h3>
                <code>{tenant.tenantId}</code>
                <p className="notice">
                  Created {new Date(tenant.createdAt).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        )}

        {nextCursor !== undefined && (
          <button
            type="button"
            disabled={loadingTenants}
            onClick={() => void loadTenants(nextCursor)}
          >
            {loadingTenants ? "Loading…" : "Load more tenants"}
          </button>
        )}
      </section>
    </>
  );
};
